<template>
  <div class="parcelles-map-container">
    <!-- En-tête avec statistiques et actions -->
    <div class="map-header">
      <div class="map-info">
        <div v-if="!loading" class="map-stats">
          <span class="stat-item">
            <strong>{{ coordonnees_parcelle_size }}</strong> parcelles géolocalisées
          </span>
          <span class="stat-item">
            <strong>{{ total_superficie.toFixed(2) }}</strong> ha
          </span>
        </div>
      </div>
      <div class="map-actions">
        <el-button
          size="small"
          icon="el-icon-search"
          type="primary"
          :loading="loading"
          @click="loadMapData"
        >
          Actualiser
        </el-button>
        <el-button
          size="small"
          icon="el-icon-download"
          type="success"
          :loading="exporting"
          @click="handleExport"
        >
          Exporter GeoJSON
        </el-button>
      </div>
    </div>

    <!-- Filtres de la carte -->
    <el-card v-if="showFilters" class="map-filters-card" shadow="never">
      <!-- Résumé des filtres appliqués -->
      <div v-if="hasActiveFilters" class="filters-summary">
        <div class="summary-header">
          <i class="el-icon-filter" />
          <span class="summary-title">Filtres actifs :</span>
        </div>
        <div class="summary-tags">
          <el-tag
            v-for="(filter, key) in activeFiltersList"
            :key="key"
            :type="getFilterTagType(key)"
            closable
            size="small"
            style="margin-right: 8px; margin-bottom: 8px;"
            @close="removeFilter(key)"
          >
            {{ filter }}
          </el-tag>
        </div>
        <div class="summary-text">
          {{ filtersSummaryText }}
        </div>
      </div>

      <el-form :inline="true" class="map-filters-form">
        <el-form-item label="Unité de production">
          <el-select
            v-model="filters.unite_production_id"
            placeholder="Toutes"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleFilterChange"
          >
            <el-option
              v-for="up in unite_productions"
              :key="up.id"
              :label="up.nom"
              :value="up.id"
              :disabled="!up.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Groupement">
          <el-select
            v-model="filters.groupement_id"
            placeholder="Tous"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleFilterChange"
          >
            <el-option
              v-for="groupement in groupements"
              :key="groupement.id"
              :label="groupement.nom"
              :value="groupement.id"
              :disabled="!groupement.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Producteur">
          <el-select
            v-model="filters.producteur_id"
            placeholder="Tous"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleProducteurChange"
          >
            <el-option
              v-for="producteur in producteurs"
              :key="producteur.id"
              :label="`${producteur.nom} ${producteur.prenom}`"
              :value="producteur.id"
              :disabled="!producteur.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Parcelle">
          <el-select
            v-model="filters.parcelle_id"
            placeholder="Toutes"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            :disabled="!filters.producteur_id"
            @change="handleFilterChange"
          >
            <el-option
              v-for="parcelle in parcelles"
              :key="parcelle.id"
              :label="parcelle.code || `Parcelle #${parcelle.id}`"
              :value="parcelle.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Région">
          <el-select
            v-model="filters.region_id"
            placeholder="Toutes"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleRegionChange"
          >
            <el-option
              v-for="region in regions"
              :key="region.id"
              :label="region.nom"
              :value="region.id"
              :disabled="!region.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Zone">
          <el-select
            v-model="filters.zone_id"
            placeholder="Toutes"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            :disabled="!filters.region_id"
            @change="handleZoneChange"
          >
            <el-option
              v-for="zone in zones"
              :key="zone.id"
              :label="zone.nom"
              :value="zone.id"
              :disabled="!zone.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Commune">
          <el-select
            v-model="filters.commune_id"
            placeholder="Toutes"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            :disabled="!filters.zone_id"
            @change="handleCommuneChange"
          >
            <el-option
              v-for="commune in communes"
              :key="commune.id"
              :label="commune.nom"
              :value="commune.id"
              :disabled="!commune.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Village">
          <el-select
            v-model="filters.village_id"
            placeholder="Tous"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            :disabled="!filters.commune_id"
            @change="handleFilterChange"
          >
            <el-option
              v-for="village in villages"
              :key="village.id"
              :label="village.nom"
              :value="village.id"
              :disabled="!village.is_active"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Statut">
          <el-select
            v-model="filters.statut"
            placeholder="Tous"
            clearable
            filterable
            size="small"
            style="width: 150px;"
            @change="handleFilterChange"
          >
            <el-option label="AB" value="AB" />
            <el-option label="C1" value="C1" />
            <el-option label="C2" value="C2" />
            <el-option label="C3" value="C3" />
          </el-select>
        </el-form-item>

        <el-form-item label="Campagne">
          <el-select
            v-model="filters.campagne_id"
            placeholder="Campagne en cours"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleFilterChange"
          >
            <el-option
              v-for="campagne in campagnes"
              :key="campagne.id"
              :label="campagne.periode"
              :value="campagne.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Produit cultivé">
          <el-select
            v-model="filters.produit_id"
            placeholder="Tous"
            clearable
            filterable
            size="small"
            style="width: 200px;"
            @change="handleFilterChange"
          >
            <el-option
              v-for="produit in produits"
              :key="produit.id"
              :label="produit.nom"
              :value="produit.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Superficie (ha)">
          <div style="width: 200px;">
            <el-slider
              v-model="superficieRange"
              range
              :min="0"
              :max="100"
              :step="0.1"
              show-stops
              @change="handleSuperficieChange"
            />
            <div style="font-size: 12px; color: #606266; margin-top: 4px;">
              {{ superficieRange[0] }} - {{ superficieRange[1] }} ha
            </div>
          </div>
        </el-form-item>

        <el-form-item label="Recherche">
          <el-input
            v-model="searchKeyword"
            placeholder="Code, libellé ou producteur..."
            clearable
            size="small"
            style="width: 250px;"
            @input="handleSearchInput"
          >
            <template #prefix>
              <i class="el-input__icon el-icon-search" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button
            size="small"
            icon="el-icon-close"
            :disabled="!hasActiveFilters"
            @click="clearFilters"
          >
            Réinitialiser
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Légende interactive -->
    <el-card v-if="showFilters && availableStatuts.length > 0" class="legend-card" shadow="never">
      <template #header>
        <div class="legend-header">
          <span>Légende des statuts</span>
        </div>
      </template>
      <div class="legend-container">
        <div
          v-for="statut in availableStatuts"
          :key="statut"
          class="legend-item"
          :class="{ active: isStatutSelected(statut) }"
          @click="toggleStatutFilter(statut)"
        >
          <span class="legend-color" :style="{ backgroundColor: statutColors[statut] || statutColors['AB'] }" />
          <span class="legend-label">{{ statut }}</span>
        </div>
      </div>
    </el-card>

    <!-- Carte Mapbox -->
    <div v-loading="loading" class="map-wrapper">
      <div id="parcellesMapContainer" class="map-container" />
    </div>
  </div>
</template>

<script>
import Resource from '@/api/resource';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import customMarker from '@/assets/marquer.png';
import ParcellePopup from './ParcellePopup.vue';
import Vue from 'vue';

const uniteProductionResource = new Resource('unite_productions');
const groupementResource = new Resource('groupements');
const producteurResource = new Resource('producteurs-all');
const parcelleResource = new Resource('parcelles');
const regionResource = new Resource('regions');
const zoneResource = new Resource('zones');
const communeResource = new Resource('communes');
const villageResource = new Resource('villages');
const coordonneeResource = new Resource('coordonnees');
const campagneResource = new Resource('campagne_agricoles');
const produitResource = new Resource('produits');

export default {
  name: 'ParcellesMap',
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      map: null,
      accessToken: 'pk.eyJ1IjoiZGV2b25lc295IiwiYSI6ImNsMTR0c2Y0MDBkdWIzZHF6cjk5OWJ1NnMifQ.cEu2E93WCC2rY74DEjqixg',
      coordonnees_parcelle: { type: 'FeatureCollection', features: [] },
      coordonnees_parcelle_size: 0,
      total_superficie: 0,
      filters: {
        unite_production_id: null,
        groupement_id: null,
        producteur_id: null,
        parcelle_id: null,
        region_id: null,
        zone_id: null,
        commune_id: null,
        village_id: null,
        statut: null,
        campagne_id: null,
        produit_id: null,
        superficie_min: null,
        superficie_max: null,
        keyword: null,
      },
      unite_productions: [],
      groupements: [],
      producteurs: [],
      parcelles: [],
      regions: [],
      zones: [],
      communes: [],
      villages: [],
      campagnes: [],
      produits: [],
      superficieRange: [0, 100],
      searchKeyword: '',
      searchDebounceTimer: null,
      query: {
        page: 1,
        limit: 3000,
      },
      statutColors: {
        'AB': '#00ab53',
        'C1': '#1890ff',
        'C2': '#fa8c16',
        'C3': '#f5222d',
      },
      selectedStatuts: [], // Statuts sélectionnés via la légende
      clusterConfig: {
        radius: 50,
        maxZoom: 14,
        minPoints: 2,
      },
      resizeObserver: null,
      mapInitialized: false,
    };
  },
  computed: {
    hasActiveFilters() {
      return Object.entries(this.filters).some(([key, value]) => {
        if (key === 'superficie_min' || key === 'superficie_max') {
          return value !== null && value !== undefined && value !== 0 && value !== 100;
        }
        return value !== null && value !== '';
      });
    },
    activeFiltersList() {
      const active = {};

      if (this.filters.unite_production_id) {
        const up = this.unite_productions.find(u => u.id === this.filters.unite_production_id);
        if (up) {
          active.unite_production_id = `Unité: ${up.nom}`;
        }
      }

      if (this.filters.groupement_id) {
        const grp = this.groupements.find(g => g.id === this.filters.groupement_id);
        if (grp) {
          active.groupement_id = `Groupement: ${grp.nom}`;
        }
      }

      if (this.filters.producteur_id) {
        const prod = this.producteurs.find(p => p.id === this.filters.producteur_id);
        if (prod) {
          active.producteur_id = `Producteur: ${prod.nom} ${prod.prenom}`;
        }
      }

      if (this.filters.parcelle_id) {
        const parc = this.parcelles.find(p => p.id === this.filters.parcelle_id);
        if (parc) {
          active.parcelle_id = `Parcelle: ${parc.code || `#${parc.id}`}`;
        }
      }

      if (this.filters.region_id) {
        const reg = this.regions.find(r => r.id === this.filters.region_id);
        if (reg) {
          active.region_id = `Région: ${reg.nom}`;
        }
      }

      if (this.filters.zone_id) {
        const zone = this.zones.find(z => z.id === this.filters.zone_id);
        if (zone) {
          active.zone_id = `Zone: ${zone.nom}`;
        }
      }

      if (this.filters.commune_id) {
        const com = this.communes.find(c => c.id === this.filters.commune_id);
        if (com) {
          active.commune_id = `Commune: ${com.nom}`;
        }
      }

      if (this.filters.village_id) {
        const vill = this.villages.find(v => v.id === this.filters.village_id);
        if (vill) {
          active.village_id = `Village: ${vill.nom}`;
        }
      }

      if (this.filters.statut) {
        active.statut = `Statut: ${this.filters.statut}`;
      }
      if (this.filters.campagne_id) {
        const camp = this.campagnes.find(c => c.id === this.filters.campagne_id);
        if (camp) {
          active.campagne_id = `Campagne: ${camp.periode}`;
        }
      }
      if (this.filters.produit_id) {
        const prod = this.produits.find(p => p.id === this.filters.produit_id);
        if (prod) {
          active.produit_id = `Produit: ${prod.nom}`;
        }
      }
      if ((this.filters.superficie_min !== null && this.filters.superficie_min !== undefined && this.filters.superficie_min !== 0) ||
          (this.filters.superficie_max !== null && this.filters.superficie_max !== undefined && this.filters.superficie_max !== 100)) {
        active.superficie = `Superficie: ${this.filters.superficie_min || 0} - ${this.filters.superficie_max || 100} ha`;
      }
      if (this.filters.keyword) {
        active.keyword = `Recherche: ${this.filters.keyword}`;
      }

      return active;
    },
    filtersSummaryText() {
      const parts = [];
      const filters = this.activeFiltersList;

      if (filters.unite_production_id) {
        parts.push(filters.unite_production_id);
      }
      if (filters.groupement_id) {
        parts.push(filters.groupement_id);
      }
      if (filters.producteur_id) {
        parts.push(filters.producteur_id);
      }
      if (filters.parcelle_id) {
        parts.push(filters.parcelle_id);
      }
      if (filters.region_id) {
        parts.push(filters.region_id);
      }
      if (filters.zone_id) {
        parts.push(filters.zone_id);
      }
      if (filters.commune_id) {
        parts.push(filters.commune_id);
      }
      if (filters.village_id) {
        parts.push(filters.village_id);
      }
      if (filters.statut) {
        parts.push(filters.statut);
      }
      if (filters.campagne_id) {
        parts.push(filters.campagne_id);
      }
      if (filters.produit_id) {
        parts.push(filters.produit_id);
      }
      if (filters.superficie) {
        parts.push(filters.superficie);
      }
      if (filters.keyword) {
        parts.push(filters.keyword);
      }

      return parts.length > 0 ? parts.join(' | ') : 'Aucun filtre appliqué';
    },
    availableStatuts() {
      // Récupérer les statuts uniques présents dans les données actuelles
      const statuts = new Set();
      const features = this.coordonnees_parcelle.features || [];
      features.forEach(feature => {
        if (feature.properties && feature.properties.Statut) {
          statuts.add(feature.properties.Statut);
        }
      });
      return Array.from(statuts).sort();
    },
    hasStatutFilter() {
      return this.selectedStatuts.length > 0;
    },
  },
  watch: {
    'filters.unite_production_id'(newVal) {
      if (newVal) {
        this.filters.groupement_id = null;
        this.filters.producteur_id = null;
        this.groupements = [];
        this.producteurs = [];
        this.handleUniteProductionChange();
      } else {
        this.getGroupementList();
      }
    },
    'filters.groupement_id'(newVal) {
      if (newVal) {
        this.filters.producteur_id = null;
        this.filters.parcelle_id = null;
        this.producteurs = [];
        this.parcelles = [];
        this.handleGroupementChange();
      } else {
        this.getProducteurList();
      }
    },
    'filters.producteur_id'(newVal) {
      if (newVal) {
        this.filters.parcelle_id = null;
        this.parcelles = [];
        this.handleProducteurChange();
      } else {
        this.parcelles = [];
      }
    },
    'filters.statut'(newVal) {
      // Synchroniser la légende avec le filtre statut
      if (newVal) {
        if (!this.selectedStatuts.includes(newVal)) {
          this.selectedStatuts = [newVal];
        }
      } else {
        // Si le filtre est effacé, effacer aussi la légende
        this.selectedStatuts = [];
      }
    },
  },
  created() {
    this.loadFilterOptions();
    this.getCampagnesList();
    this.getProduitsList();
  },
  mounted() {
    // Attendre que le conteneur soit visible avant d'initialiser la carte
    // Le composant peut être monté alors que le parent utilise v-show="!loading"
    this.waitForContainerVisible(() => {
      this.initializeMap();
      this.loadMapData();
    });
  },
  beforeDestroy() {
    // Nettoyer le ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    async loadFilterOptions() {
      try {
        await Promise.all([
          this.getUniteProductionsList(),
          this.getRegionList(),
        ]);
      } catch (error) {
        console.error('Erreur lors du chargement des options de filtres:', error);
      }
    },

    async getUniteProductionsList() {
      try {
        const { data } = await uniteProductionResource.sublist('?typeApi="all"', this.query);
        this.unite_productions = data || [];
      } catch (error) {
        console.error('Erreur chargement unités de production:', error);
      }
    },

    async getGroupementList() {
      try {
        let url = '?typeApi="all"';
        if (this.filters.unite_production_id) {
          url = `?unite_production_id=${this.filters.unite_production_id}`;
        }
        const { data } = await groupementResource.sublist(url, this.query);
        this.groupements = data || [];
      } catch (error) {
        console.error('Erreur chargement groupements:', error);
      }
    },

    async getProducteurList() {
      try {
        let url = '?typeApi="all"';
        if (this.filters.groupement_id) {
          url = `?groupement_id=${this.filters.groupement_id}`;
        } else if (this.filters.unite_production_id) {
          url = `?unite_production_id=${this.filters.unite_production_id}`;
        }
        const { data } = await producteurResource.sublist(url, this.query);
        this.producteurs = data || [];
      } catch (error) {
        console.error('Erreur chargement producteurs:', error);
      }
    },

    async getParcelleList() {
      if (!this.filters.producteur_id) {
        this.parcelles = [];
        return;
      }
      try {
        const queryParams = {
          producteur_id: this.filters.producteur_id,
          limit: 1000,
        };
        const response = await parcelleResource.list(queryParams);
        // L'API retourne les parcelles dans response.data
        if (response && response.data) {
          this.parcelles = Array.isArray(response.data) ? response.data : [];
        } else {
          this.parcelles = [];
        }
      } catch (error) {
        console.error('Erreur chargement parcelles:', error);
        this.parcelles = [];
      }
    },

    async getRegionList() {
      try {
        const { data } = await regionResource.sublist('?typeApi="all"', this.query);
        this.regions = data || [];
      } catch (error) {
        console.error('Erreur chargement régions:', error);
      }
    },

    async getZoneList() {
      try {
        let url = '?typeApi="all"';
        if (this.filters.region_id) {
          url = `?region_id=${this.filters.region_id}`;
        }
        const { data } = await zoneResource.sublist(url, this.query);
        this.zones = data || [];
      } catch (error) {
        console.error('Erreur chargement zones:', error);
      }
    },

    async getCommuneList() {
      try {
        let url = '?typeApi="all"';
        if (this.filters.zone_id) {
          url = `?zone_id=${this.filters.zone_id}`;
        }
        const { data } = await communeResource.sublist(url, this.query);
        this.communes = data || [];
      } catch (error) {
        console.error('Erreur chargement communes:', error);
      }
    },

    async getVillageList() {
      try {
        let url = '';
        if (this.filters.commune_id) {
          url = `?commune_id=${this.filters.commune_id}`;
        }
        const { data } = await villageResource.list(url ? { commune_id: this.filters.commune_id } : this.query);
        this.villages = data || [];
      } catch (error) {
        console.error('Erreur chargement villages:', error);
      }
    },

    async getCampagnesList() {
      try {
        const { data } = await campagneResource.list({ limit: 100 });
        this.campagnes = data || [];
      } catch (error) {
        console.error('Erreur chargement campagnes:', error);
      }
    },

    async getProduitsList() {
      try {
        const { data } = await produitResource.list({ limit: 1000 });
        this.produits = data || [];
      } catch (error) {
        console.error('Erreur chargement produits:', error);
      }
    },

    handleSuperficieChange(value) {
      this.filters.superficie_min = value[0];
      this.filters.superficie_max = value[1];
      this.handleFilterChange();
    },

    handleSearchInput() {
      // Debounce de 300ms
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.filters.keyword = this.searchKeyword || null;
        this.handleFilterChange();
      }, 300);
    },

    handleFilterChange() {
      // Synchroniser le filtre statut avec la légende
      if (this.filters.statut) {
        if (!this.selectedStatuts.includes(this.filters.statut)) {
          this.selectedStatuts = [this.filters.statut];
        }
      } else if (this.selectedStatuts.length === 1 && this.filters.statut === null) {
        // Si le filtre est effacé et qu'un seul statut était sélectionné, effacer aussi la légende
        this.selectedStatuts = [];
      }

      // Faire un fly to vers l'unité de production si sélectionnée
      if (this.filters.unite_production_id && this.map) {
        this.flyToUniteProduction(this.filters.unite_production_id);
      }

      this.loadMapData();
    },

    async handleProducteurChange() {
      this.filters.parcelle_id = null;
      this.parcelles = [];
      if (this.filters.producteur_id) {
        await this.getParcelleList();
      }
      this.loadMapData();
    },

    async handleRegionChange() {
      this.filters.zone_id = null;
      this.filters.commune_id = null;
      this.filters.village_id = null;
      this.zones = [];
      this.communes = [];
      this.villages = [];
      await this.getZoneList();

      // Faire un fly to sur la région si sélectionnée
      if (this.filters.region_id && this.map) {
        this.flyToRegion(this.filters.region_id);
      }

      this.loadMapData();
    },

    async flyToRegion(regionId) {
      try {
        // Récupérer les coordonnées des parcelles pour cette région
        const params = [`region_id=${regionId}`];
        const subUrl = `/recherche?${params.join('&')}`;
        const response = await coordonneeResource.sublist(subUrl, { limit: 1000 });

        const geojson = response.geojson || { type: 'FeatureCollection', features: [] };
        const features = geojson.features || [];

        if (features.length === 0) {
          console.warn('Aucune parcelle trouvée pour cette région');
          return;
        }

        // Calculer le centre géographique (centroid) de toutes les parcelles
        let totalLat = 0;
        let totalLng = 0;
        let pointCount = 0;

        features.forEach(feature => {
          if (feature.geometry.type === 'Point') {
            const [lng, lat] = feature.geometry.coordinates;
            totalLat += lat;
            totalLng += lng;
            pointCount++;
          } else if (feature.geometry.type === 'Polygon') {
            // Pour les polygones, calculer le centre
            const coordinates = feature.geometry.coordinates[0];
            let polyLat = 0;
            let polyLng = 0;
            coordinates.forEach(coord => {
              polyLat += coord[1];
              polyLng += coord[0];
            });
            totalLat += polyLat / coordinates.length;
            totalLng += polyLng / coordinates.length;
            pointCount++;
          }
        });

        if (pointCount > 0) {
          const centerLat = totalLat / pointCount;
          const centerLng = totalLng / pointCount;

          // Faire un fly to vers le centre de la région
          this.map.flyTo({
            center: [centerLng, centerLat],
            zoom: 9,
            speed: 1.2,
            curve: 1.4,
            easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
          });
        }
      } catch (error) {
        console.error('Erreur lors du fly to vers la région:', error);
      }
    },

    async flyToUniteProduction(uniteProductionId) {
      try {
        // Récupérer les coordonnées des parcelles pour cette unité de production
        const params = [`unite_production_id=${uniteProductionId}`];
        const subUrl = `/recherche?${params.join('&')}`;
        const response = await coordonneeResource.sublist(subUrl, { limit: 1000 });

        const geojson = response.geojson || { type: 'FeatureCollection', features: [] };
        const features = geojson.features || [];

        if (features.length === 0) {
          console.warn('Aucune parcelle trouvée pour cette unité de production');
          return;
        }

        // Calculer le centre géographique (centroid) de toutes les parcelles
        let totalLat = 0;
        let totalLng = 0;
        let pointCount = 0;

        features.forEach(feature => {
          if (feature.geometry.type === 'Point') {
            const [lng, lat] = feature.geometry.coordinates;
            totalLat += lat;
            totalLng += lng;
            pointCount++;
          } else if (feature.geometry.type === 'Polygon') {
            // Pour les polygones, calculer le centre
            const coordinates = feature.geometry.coordinates[0];
            let polyLat = 0;
            let polyLng = 0;
            coordinates.forEach(coord => {
              polyLat += coord[1];
              polyLng += coord[0];
            });
            totalLat += polyLat / coordinates.length;
            totalLng += polyLng / coordinates.length;
            pointCount++;
          }
        });

        if (pointCount > 0) {
          const centerLat = totalLat / pointCount;
          const centerLng = totalLng / pointCount;

          // Faire un fly to vers le centre de l'unité de production
          this.map.flyTo({
            center: [centerLng, centerLat],
            zoom: 9,
            speed: 1.2,
            curve: 1.4,
            easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
          });
        }
      } catch (error) {
        console.error('Erreur lors du fly to vers l\'unité de production:', error);
      }
    },

    async handleZoneChange() {
      this.filters.commune_id = null;
      this.filters.village_id = null;
      this.communes = [];
      this.villages = [];
      await this.getCommuneList();
      this.loadMapData();
    },

    async handleCommuneChange() {
      this.filters.village_id = null;
      this.villages = [];
      await this.getVillageList();
      this.loadMapData();
    },

    async handleUniteProductionChange() {
      this.filters.groupement_id = null;
      this.filters.producteur_id = null;
      this.filters.parcelle_id = null;
      this.groupements = [];
      this.producteurs = [];
      this.parcelles = [];
      await this.getGroupementList();
      this.loadMapData();
    },

    async handleGroupementChange() {
      this.filters.producteur_id = null;
      this.filters.parcelle_id = null;
      this.producteurs = [];
      this.parcelles = [];
      await this.getProducteurList();
      this.loadMapData();
    },

    clearFilters() {
      this.filters = {
        unite_production_id: null,
        groupement_id: null,
        producteur_id: null,
        parcelle_id: null,
        region_id: null,
        zone_id: null,
        commune_id: null,
        village_id: null,
        statut: null,
        campagne_id: null,
        produit_id: null,
        superficie_min: null,
        superficie_max: null,
        keyword: null,
      };
      this.selectedStatuts = [];
      this.groupements = [];
      this.producteurs = [];
      this.parcelles = [];
      this.zones = [];
      this.communes = [];
      this.villages = [];
      this.superficieRange = [0, 100];
      this.searchKeyword = '';
      this.loadMapData();
    },

    removeFilter(filterKey) {
      // Retirer le filtre spécifique
      this.filters[filterKey] = null;

      // Si on retire le filtre statut, effacer aussi la légende
      if (filterKey === 'statut') {
        this.selectedStatuts = [];
      }

      // Gérer les dépendances en cascade
      if (filterKey === 'region_id') {
        this.filters.zone_id = null;
        this.filters.commune_id = null;
        this.filters.village_id = null;
        this.zones = [];
        this.communes = [];
        this.villages = [];
      } else if (filterKey === 'zone_id') {
        this.filters.commune_id = null;
        this.filters.village_id = null;
        this.communes = [];
        this.villages = [];
      } else if (filterKey === 'commune_id') {
        this.filters.village_id = null;
        this.villages = [];
      } else if (filterKey === 'unite_production_id') {
        this.filters.groupement_id = null;
        this.filters.producteur_id = null;
        this.filters.parcelle_id = null;
        this.groupements = [];
        this.producteurs = [];
        this.parcelles = [];
      } else if (filterKey === 'groupement_id') {
        this.filters.producteur_id = null;
        this.filters.parcelle_id = null;
        this.producteurs = [];
        this.parcelles = [];
      } else if (filterKey === 'producteur_id') {
        this.filters.parcelle_id = null;
        this.parcelles = [];
      } else if (filterKey === 'superficie_min' || filterKey === 'superficie_max') {
        this.superficieRange = [this.filters.superficie_min || 0, this.filters.superficie_max || 100];
      } else if (filterKey === 'keyword') {
        this.searchKeyword = '';
      }

      // Recharger les options si nécessaire
      if (filterKey === 'region_id' || filterKey === 'zone_id' || filterKey === 'commune_id') {
        this.getZoneList();
        this.getCommuneList();
        this.getVillageList();
      }
      if (filterKey === 'unite_production_id' || filterKey === 'groupement_id') {
        this.getGroupementList();
        this.getProducteurList();
      }
      if (filterKey === 'producteur_id') {
        this.getParcelleList();
      }

      this.loadMapData();
    },

    getFilterTagType(filterKey) {
      // Différents types de tags selon le type de filtre
      const typeMap = {
        unite_production_id: 'primary',
        groupement_id: 'success',
        producteur_id: 'info',
        parcelle_id: 'warning',
        region_id: 'warning',
        zone_id: '',
        commune_id: '',
        village_id: '',
        statut: 'danger',
      };
      return typeMap[filterKey] || '';
    },

    getColorByStatut(statut) {
      return this.statutColors[statut] || this.statutColors['AB'];
    },

    getColorExpression() {
      return [
        'match',
        ['get', 'Statut'],
        'AB', this.statutColors.AB,
        'C1', this.statutColors.C1,
        'C2', this.statutColors.C2,
        'C3', this.statutColors.C3,
        this.statutColors.AB, // default
      ];
    },

    filterFeaturesByStatut(features) {
      if (!this.hasStatutFilter) {
        return features;
      }
      return features.filter(feature => {
        const statut = feature.properties && feature.properties.Statut;
        return statut && this.selectedStatuts.includes(statut);
      });
    },

    isStatutSelected(statut) {
      return this.selectedStatuts.includes(statut);
    },

    toggleStatutFilter(statut) {
      const index = this.selectedStatuts.indexOf(statut);
      if (index > -1) {
        this.selectedStatuts.splice(index, 1);
      } else {
        this.selectedStatuts.push(statut);
      }
      // Synchroniser avec le filtre existant
      if (this.selectedStatuts.length === 1) {
        this.filters.statut = this.selectedStatuts[0];
      } else if (this.selectedStatuts.length === 0) {
        this.filters.statut = null;
      } else {
        // Si plusieurs statuts sélectionnés, on ne peut pas utiliser le filtre simple
        // On garde le filtre null et on filtre côté client
        this.filters.statut = null;
      }
      this.loadMapData();
    },

    handleClusterClick(e) {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });

      if (features.length > 0) {
        const clusterId = features[0].properties.cluster_id;
        const source = this.map.getSource('places');
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) {
            return;
          }

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
      }
    },

    /**
     * Attend que le conteneur soit visible avant d'initialiser la carte
     * Résout le problème où v-show="!loading" cache le conteneur au montage
     */
    waitForContainerVisible(callback) {
      const container = this.$el;
      if (!container) {
        this.$nextTick(() => this.waitForContainerVisible(callback));
        return;
      }

      // Vérifier si le conteneur est déjà visible
      const isVisible = () => {
        const rect = container.getBoundingClientRect();
        const style = window.getComputedStyle(container);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0'
        );
      };

      if (isVisible()) {
        // Le conteneur est visible, initialiser après un court délai
        this.$nextTick(() => {
          setTimeout(callback, 100);
        });
        return;
      }

      // Le conteneur n'est pas visible, utiliser un ResizeObserver pour détecter quand il le devient
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0 && isVisible()) {
            // Le conteneur est maintenant visible et a une taille
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
            this.$nextTick(() => {
              setTimeout(callback, 100);
            });
            break;
          }
        }
      });

      this.resizeObserver.observe(container);

      // Fallback : si après 2 secondes le conteneur n'est toujours pas visible, initialiser quand même
      setTimeout(() => {
        if (!this.mapInitialized && this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
          this.$nextTick(() => {
            setTimeout(callback, 100);
          });
        }
      }, 2000);
    },

    initializeMap() {
      if (this.mapInitialized) {
        return; // Éviter les initialisations multiples
      }

      mapboxgl.accessToken = this.accessToken;

      this.map = new mapboxgl.Map({
        container: 'parcellesMapContainer',
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [0.824782, 8.619543], // Centre du Togo
        zoom: 6.4,
      });

      this.mapInitialized = true;

      // Ajouter les contrôles
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
      this.map.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
        }),
        'top-right'
      );

      // Correction du dimensionnement : appeler resize() après que la carte soit chargée
      // et que le conteneur ait sa taille finale
      this.map.once('load', () => {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.map && this.map.loaded()) {
              this.map.resize();
            }
          }, 150);
        });
      });

      // Également appeler resize() après le premier rendu complet
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.map && this.map.loaded()) {
            this.map.resize();
          }
        }, 300);
      });

      // Utiliser un ResizeObserver pour détecter les changements de taille du conteneur
      const mapContainer = document.getElementById('parcellesMapContainer');
      if (mapContainer) {
        const mapResizeObserver = new ResizeObserver(() => {
          if (this.map && this.map.loaded()) {
            this.map.resize();
          }
        });
        mapResizeObserver.observe(mapContainer);
      }
    },

    async loadMapData() {
      this.loading = true;

      try {
        // Construire l'URL de recherche avec les filtres
        const params = [];
        if (this.filters.unite_production_id) {
          params.push(`unite_production_id=${this.filters.unite_production_id}`);
        }
        if (this.filters.groupement_id) {
          params.push(`groupement_id=${this.filters.groupement_id}`);
        }
        if (this.filters.producteur_id) {
          params.push(`producteur_id=${this.filters.producteur_id}`);
        }
        if (this.filters.parcelle_id) {
          params.push(`parcelle_id=${this.filters.parcelle_id}`);
        }
        if (this.filters.region_id) {
          params.push(`region_id=${this.filters.region_id}`);
        }
        if (this.filters.zone_id) {
          params.push(`zone_id=${this.filters.zone_id}`);
        }
        if (this.filters.commune_id) {
          params.push(`commune_id=${this.filters.commune_id}`);
        }
        if (this.filters.village_id) {
          params.push(`village_id=${this.filters.village_id}`);
        }
        if (this.filters.statut) {
          params.push(`statut=${this.filters.statut}`);
        }
        if (this.filters.campagne_id) {
          params.push(`campagne_id=${this.filters.campagne_id}`);
        }
        if (this.filters.produit_id) {
          params.push(`produit_id=${this.filters.produit_id}`);
        }
        if (this.filters.superficie_min !== null && this.filters.superficie_min !== undefined) {
          params.push(`superficie_min=${this.filters.superficie_min}`);
        }
        if (this.filters.superficie_max !== null && this.filters.superficie_max !== undefined) {
          params.push(`superficie_max=${this.filters.superficie_max}`);
        }
        if (this.filters.keyword) {
          params.push(`keyword=${encodeURIComponent(this.filters.keyword)}`);
        }

        const subUrl = `/recherche?${params.join('&')}`;
        const response = await coordonneeResource.sublist(subUrl, this.query);

        this.coordonnees_parcelle = response.geojson || { type: 'FeatureCollection', features: [] };
        this.total_superficie = response.superficie_total || 0;
        this.coordonnees_parcelle_size = this.coordonnees_parcelle.features?.length || 0;

        // Mettre à jour la carte
        if (this.map) {
          this.updateMap();
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de la carte:', error);
        this.$message.error('Erreur lors du chargement des données de la carte');
      } finally {
        this.loading = false;
      }
    },

    updateMap() {
      if (!this.map || !this.map.loaded()) {
        this.map.on('load', () => this.updateMap());
        return;
      }

      // S'assurer que la carte a les bonnes dimensions avant de mettre à jour
      // Cela corrige les cas où le conteneur a changé de taille après l'initialisation
      this.$nextTick(() => {
        if (this.map && this.map.loaded()) {
          this.map.resize();
        }
      });

      // Supprimer les sources et layers existants
      if (this.map.getSource('places')) {
        // Supprimer toutes les couches liées aux points
        const pointLayers = ['places', 'clusters', 'cluster-count', 'unclustered-point'];
        pointLayers.forEach(layerId => {
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId);
          }
        });
        this.map.removeSource('places');
      }

      if (this.map.getSource('parcelles')) {
        if (this.map.getLayer('parcelles-fill')) {
          this.map.removeLayer('parcelles-fill');
        }
        if (this.map.getLayer('parcelles-outline')) {
          this.map.removeLayer('parcelles-outline');
        }
        if (this.map.getLayer('parcelles-layer')) {
          this.map.removeLayer('parcelles-layer');
        }
        if (this.map.getLayer('parcelles-label')) {
          this.map.removeLayer('parcelles-label');
        }
        this.map.removeSource('parcelles');
      }

      const features = this.coordonnees_parcelle.features || [];
      // Filtrer par statut si nécessaire (via légende)
      const filteredFeatures = this.filterFeaturesByStatut(features);
      const pointFeatures = filteredFeatures.filter(f => f.geometry.type === 'Point');
      const polygonFeatures = filteredFeatures.filter(f => f.geometry.type === 'Polygon');

      // Charger l'image du marqueur
      this.map.loadImage(customMarker, (error, image) => {
        if (error) {
          console.error('Erreur chargement image marqueur:', error);
        } else {
          if (!this.map.hasImage('parcelle-icon')) {
            this.map.addImage('parcelle-icon', image);
          }
        }

        // Ajouter les points avec clustering
        if (pointFeatures.length > 0) {
          this.map.addSource('places', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: pointFeatures,
            },
            cluster: true,
            clusterRadius: this.clusterConfig.radius,
            clusterMaxZoom: this.clusterConfig.maxZoom,
            clusterMinPoints: this.clusterConfig.minPoints,
          });

          // Couche de clusters
          this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'places',
            filter: ['has', 'point_count'],
            paint: {
              'circle-color': [
                'step',
                ['get', 'point_count'],
                '#51cf66',
                10,
                '#4dabf7',
                30,
                '#ff922b',
                50,
                '#ff6b6b',
              ],
              'circle-radius': [
                'step',
                ['get', 'point_count'],
                15,
                10,
                20,
                30,
                25,
                50,
                30,
              ],
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            },
          });

          // Labels pour les clusters
          this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'places',
            filter: ['has', 'point_count'],
            layout: {
              'text-field': '{point_count_abbreviated}',
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              'text-size': 12,
            },
            paint: {
              'text-color': '#ffffff',
            },
          });

          // Points individuels non-clustérisés
          this.map.addLayer({
            id: 'unclustered-point',
            type: 'circle',
            source: 'places',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-color': this.getColorExpression(),
              'circle-radius': 6,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            },
          });

          // Clic sur cluster : zoomer
          this.map.on('click', 'clusters', this.handleClusterClick);

          // Clic sur point individuel : popup
          const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
          this.map.on('click', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const coordinates = e.features[0].geometry.coordinates.slice();
            const feature = e.features[0];
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            const popupContent = this.createPopupContent(feature);
            popup.setLngLat(coordinates).setDOMContent(popupContent).addTo(this.map);
          });

          // Hover sur clusters
          this.map.on('mouseenter', 'clusters', () => {
            this.map.getCanvas().style.cursor = 'pointer';
          });

          this.map.on('mouseleave', 'clusters', () => {
            this.map.getCanvas().style.cursor = '';
          });

          // Hover sur points individuels
          this.map.on('mouseenter', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = 'pointer';
          });

          this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
          });
        }

        // Ajouter les polygones
        if (polygonFeatures.length > 0) {
          this.map.addSource('parcelles', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: polygonFeatures,
            },
          });

          // Couche de remplissage avec couleur par statut
          this.map.addLayer({
            id: 'parcelles-fill',
            type: 'fill',
            source: 'parcelles',
            paint: {
              'fill-color': this.getColorExpression(),
              'fill-opacity': 0.3,
            },
          });

          // Couche de contour avec couleur par statut
          this.map.addLayer({
            id: 'parcelles-outline',
            type: 'line',
            source: 'parcelles',
            paint: {
              'line-color': this.getColorExpression(),
              'line-width': 2,
            },
          });

          // Couche d'icônes sur les polygones
          if (this.map.hasImage('parcelle-icon')) {
            this.map.addLayer({
              id: 'parcelles-layer',
              type: 'symbol',
              source: 'parcelles',
              layout: {
                'icon-image': 'parcelle-icon',
                'icon-size': 0.5,
                'icon-allow-overlap': true,
              },
            });
          }

          // Popup pour les polygones
          const popupPoly = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
          ['parcelles-fill', 'parcelles-outline', 'parcelles-layer'].forEach(layerId => {
            if (this.map.getLayer(layerId)) {
              this.map.on('click', layerId, (e) => {
                this.map.getCanvas().style.cursor = 'pointer';
                const coordinates = e.lngLat;
                const feature = e.features[0];
                const popupContent = this.createPopupContent(feature);
                popupPoly.setLngLat(coordinates).setDOMContent(popupContent).addTo(this.map);
              });

              this.map.on('mouseenter', layerId, () => {
                this.map.getCanvas().style.cursor = 'pointer';
              });

              this.map.on('mouseleave', layerId, () => {
                this.map.getCanvas().style.cursor = '';
              });
            }
          });
        }

        // Zoomer vers les parcelles affichées après un court délai pour s'assurer que les sources sont chargées
        setTimeout(() => {
          this.fitMapToFeatures();
        }, 300);
      });
    },

    fitMapToFeatures() {
      if (!this.map || !this.map.loaded()) {
        return;
      }

      const features = this.coordonnees_parcelle.features || [];

      if (features.length === 0) {
        // Si aucune parcelle, revenir à la vue par défaut
        this.map.flyTo({
          center: [0.824782, 8.619543],
          zoom: 6.4,
          duration: 1000,
        });
        return;
      }

      // Calculer les bounds de toutes les parcelles
      const bounds = new mapboxgl.LngLatBounds();

      features.forEach(feature => {
        if (feature.geometry.type === 'Point') {
          // Pour les points, ajouter directement les coordonnées
          const [lng, lat] = feature.geometry.coordinates;
          bounds.extend([lng, lat]);
        } else if (feature.geometry.type === 'Polygon') {
          // Pour les polygones, parcourir tous les points
          const coordinates = feature.geometry.coordinates[0]; // Premier ring du polygone
          if (coordinates && Array.isArray(coordinates)) {
            coordinates.forEach(coord => {
              if (Array.isArray(coord) && coord.length >= 2) {
                const [lng, lat] = coord;
                bounds.extend([lng, lat]);
              }
            });
          }
        }
      });

      // Si on a des bounds valides, zoomer vers elles
      if (!bounds.isEmpty()) {
        // Ajouter un padding pour que les parcelles ne soient pas collées aux bords
        const padding = {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        };

        this.map.fitBounds(bounds, {
          padding: padding,
          duration: 1500, // Animation de 1.5 secondes
          maxZoom: 18, // Zoom maximum pour éviter de zoomer trop près
        });
      } else {
        // Si pas de bounds valides, revenir à la vue par défaut
        this.map.flyTo({
          center: [0.824782, 8.619543],
          zoom: 6.4,
          duration: 1000,
        });
      }
    },

    async handleExport() {
      try {
        this.exporting = true;
        const params = [];
        if (this.filters.unite_production_id) {
          params.push(`unite_production_id=${this.filters.unite_production_id}`);
        }
        if (this.filters.groupement_id) {
          params.push(`groupement_id=${this.filters.groupement_id}`);
        }
        if (this.filters.producteur_id) {
          params.push(`producteur_id=${this.filters.producteur_id}`);
        }
        if (this.filters.parcelle_id) {
          params.push(`parcelle_id=${this.filters.parcelle_id}`);
        }
        if (this.filters.region_id) {
          params.push(`region_id=${this.filters.region_id}`);
        }
        if (this.filters.zone_id) {
          params.push(`zone_id=${this.filters.zone_id}`);
        }
        if (this.filters.commune_id) {
          params.push(`commune_id=${this.filters.commune_id}`);
        }
        if (this.filters.village_id) {
          params.push(`village_id=${this.filters.village_id}`);
        }
        if (this.filters.statut) {
          params.push(`statut=${this.filters.statut}`);
        }

        const subUrl = `/export-geojson?${params.join('&')}`;
        const data = await coordonneeResource.sublist(subUrl, this.query);

        if (data) {
          this.downloadGeojson(data);
          this.$message.success('Export GeoJSON réussi');
        } else {
          this.$message.warning('Aucune donnée trouvée pour l\'export');
        }
      } catch (error) {
        console.error('Erreur export GeoJSON:', error);
        this.$message.error('Erreur lors de l\'export des données');
      } finally {
        this.exporting = false;
      }
    },

    downloadGeojson(data) {
      const year = new Date().getFullYear();
      const now = new Date();
      const timestamp = `${year}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      const filename = `${year}_eudr_parcelles_${timestamp}.geojson`;

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    createPopupContent(feature) {
      // Créer un élément DOM pour le composant Vue
      const popupDiv = document.createElement('div');

      // Créer une instance du composant ParcellePopup
      const PopupComponent = Vue.extend(ParcellePopup);
      const popupInstance = new PopupComponent({
        propsData: {
          feature: feature,
        },
        parent: this,
      });

      // Monter le composant sur l'élément DOM
      popupInstance.$mount();
      popupDiv.appendChild(popupInstance.$el);

      return popupDiv;
    },
  },
};
</script>

<style lang="scss" scoped>
.parcelles-map-container {
  width: 100%;
  height: 100%;

  .map-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px 0;

    .map-info {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .map-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #00ab53;
          font-size: 20px;
        }
      }

      .map-stats {
        display: flex;
        gap: 20px;
        font-size: 14px;
        color: #606266;

        .stat-item {
          strong {
            color: #00ab53;
            font-weight: 600;
          }
        }
      }
    }

    .map-actions {
      display: flex;
      gap: 8px;
    }
  }

  .map-filters-card {
    margin-bottom: 16px;
    border-radius: 8px;

    .filters-summary {
      padding: 12px 16px;
      margin-bottom: 16px;
      background: #f5f7fa;
      border-radius: 6px;
      border-left: 4px solid #00ab53;

      .summary-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        font-weight: 600;
        color: #303133;
        font-size: 14px;

        i {
          color: #00ab53;
          font-size: 16px;
        }

        .summary-title {
          color: #606266;
        }
      }

      .summary-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
      }

      .summary-text {
        padding: 8px 12px;
        background: #fff;
        border-radius: 4px;
        font-size: 13px;
        color: #606266;
        border: 1px solid #e4e7ed;
        font-style: italic;
      }
    }

    .map-filters-form {
      ::v-deep .el-form-item {
        margin-bottom: 12px;
      }
    }
  }

  .legend-card {
    margin-bottom: 16px;
    border-radius: 8px;

    .legend-header {
      font-weight: 600;
      color: #303133;
      font-size: 14px;
    }

    .legend-container {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      padding: 8px 0;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;
        border: 1px solid #e4e7ed;
        background: #fff;

        &:hover {
          background: #f5f7fa;
          border-color: #00ab53;
        }

        &.active {
          background: #e6f7ff;
          border-color: #1890ff;
          font-weight: 600;
        }

        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .legend-label {
          font-size: 13px;
          color: #606266;
        }
      }
    }
  }

  .map-wrapper {
    position: relative;
    width: 100%;
    height: 600px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .map-container {
      width: 100%;
      height: 100%;
    }
  }
}

::v-deep .mapboxgl-popup {
  max-width: 400px;
  font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
}

@media (max-width: 768px) {
  .parcelles-map-container {
    .map-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;

      .map-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }

    .legend-card {
      .legend-container {
        .legend-item {
          font-size: 12px;
          padding: 4px 8px;
        }
      }
    }

    .map-wrapper {
      height: 400px;
    }
  }
}
</style>

